import {
  EuiButtonIcon,
  EuiLink,
  EuiPopover,
  EuiText,
} from "@inscopix/ideas-eui";
import { DataTableColumnDefinition } from "../store/DataTableProvider.types";
import { useState } from "react";
import { useDataTableContext } from "../store/DataTableProvider";
import { captureException } from "@sentry/react";
import { GridApi } from "ag-grid-community";
import { DataTableRowData } from "../layout/DataTable";

interface ColumnHeaderBaseMetadatumLinkProps {
  columnDefinition: Extract<DataTableColumnDefinition, { kind: "metadatum" }>;
  gridApi: GridApi<DataTableRowData>;
}

export const ColumnHeaderBaseMetadatumLink = ({
  columnDefinition,
  gridApi,
}: ColumnHeaderBaseMetadatumLinkProps) => {
  const jumpToColumn = useDataTableContext((s) => s.jumpToColumn);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const fileColumn = useDataTableContext(
    (s) => {
      const columnId = columnDefinition.file_column?.id;
      for (const table of s.tables) {
        for (const column of table.columns) {
          if (column.id === columnId) {
            return {
              id: column.id,
              name: column.name,
              tableId: table.id,
              tableName: table.name,
            };
          }
        }
      }
    },
    // Only recalculate if name is changed or column is deleted
    (a, b) => a?.name === b?.name,
  );
  // Display nothing if file column was deleted
  if (fileColumn === undefined) {
    return null;
  }

  const handleClick = (options: { tableId: string; columnId: string }) => {
    /**
     * 2026-04-21
     * There was previously logic to be able to jump to cells in other tables, and the column find
     * logic above looks through all tables even though file metadata columns should only ever be linked
     * from the same table.
     * The jump to column helper now makes the assumption
     */
    if (gridApi.getGridId() !== options.tableId) {
      captureException("Tried to jump to column on an inactive grid", {
        extra: { ...options, gridId: gridApi.getGridId() },
      });
    }
    jumpToColumn({
      gridApi,
      columnId: options.columnId,
    });

    setIsPopoverOpen(false);
  };

  return (
    <EuiPopover
      button={
        <EuiButtonIcon
          aria-label="Join column source"
          color="primary"
          size="xs"
          iconSize="s"
          iconType="link"
          onClick={() => setIsPopoverOpen((isOpen) => !isOpen)}
        />
      }
      isOpen={isPopoverOpen}
      closePopover={() => setIsPopoverOpen(false)}
      anchorPosition="downCenter"
      ownFocus={false}
    >
      <div style={{ width: "300px" }}>
        <EuiText size="s">
          <p>
            Metadata in this column is read from the cells in the{" "}
            <EuiLink
              onClick={() =>
                handleClick({
                  tableId: fileColumn.tableId,
                  columnId: fileColumn.id,
                })
              }
            >
              {fileColumn.name}
            </EuiLink>{" "}
            column.
          </p>
        </EuiText>
      </div>
    </EuiPopover>
  );
};
